import { useEffect, useState, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackPurchase } from "@/lib/analytics";
import { GoldCoin } from "@/components/CoinIcons";

export default function GoldStore() {
  const { refreshUser } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then((r) => {
      setCfg(r.data);
      setSelected((r.data.gold_packages || [])[0]?.id || null);
    }).catch(() => setCfg(false));
  }, []);

  // Memoize PayPal options to prevent unnecessary provider remounts
  const paypalOptions = useMemo(() => {
    if (!cfg?.client_id) return null;
    return {
      clientId: cfg.client_id,
      currency: cfg.currency || "USD",
      intent: "capture",
      components: "buttons"
    };
  }, [cfg?.client_id, cfg?.currency]);

  if (cfg === null) return <div className="text-xs text-zinc-500 py-2" data-testid="gold-loading">Loading…</div>;
  if (!cfg || !cfg.client_id || !(cfg.gold_packages || []).length || !paypalOptions) return null;

  const packages = cfg.gold_packages;
  const pkg = packages.find((p) => p.id === selected) || packages[0];
  const maxBonus = Math.max(...packages.map((p) => p.bonus_pct || 0));

  return (
    <div data-testid="gold-store" className="border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-6 mb-8">
      <div className="flex items-center gap-2 mb-1">
        <GoldCoin size={20} />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-xl">Buy Gold</h3>
      </div>
      <p className="text-sm text-zinc-400 mb-5">Top up your coffers instantly. Gold is added to your account the moment your payment clears.</p>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {packages.map((p) => {
          const active = p.id === selected;
          const best = (p.bonus_pct || 0) > 0 && p.bonus_pct === maxBonus;
          return (
            <button key={p.id} type="button" data-testid={`gold-pkg-${p.id}`} onClick={() => setSelected(p.id)}
              className={`relative text-left p-4 rounded-sm border transition-all ${active ? "border-amber-500 bg-amber-900/20" : "border-zinc-800 bg-zinc-950 hover:border-amber-700/60"}`}>
              {best && (
                <span data-testid={`gold-best-${p.id}`} className="absolute -top-2 right-2 text-[9px] uppercase tracking-wider font-cinzel text-zinc-950 bg-amber-400 px-1.5 py-0.5 rounded-sm">Best Value</span>
              )}
              <div className="flex items-center gap-1.5 font-cinzel text-amber-300 text-lg">
                <GoldCoin size={15} className="text-amber-400" /> {Number(p.gold).toLocaleString()}
              </div>
              <div className="text-xs text-zinc-500 uppercase tracking-wide mt-0.5">Gold</div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-emerald-300 font-semibold">${p.price}</span>
                {(p.bonus_pct || 0) > 0 && (
                  <span data-testid={`gold-bonus-${p.id}`} className="text-[10px] uppercase tracking-wide text-sky-300 border border-sky-500/40 bg-sky-950/30 px-1.5 py-0.5 rounded-sm">+{p.bonus_pct}% value</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-zinc-950/60 border border-amber-900/40 p-4 rounded-sm max-w-sm">
        <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3 text-center" data-testid="gold-selected-label">
          Pay ${pkg.price} for {Number(pkg.gold).toLocaleString()} gold
        </p>
        <PayPalScriptProvider options={paypalOptions}>
          <PayPalButtons
            key={pkg.id}
            style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
            createOrder={async () => {
              const r = await api.post("/paypal/gold/create-order", { package_id: pkg.id });
              return r.data.order_id;
            }}
            onApprove={async (data) => {
              try {
                const r = await api.post("/paypal/gold/capture", { order_id: data.orderID });
                trackPurchase({ value: Number(pkg.price), currency: cfg.currency || "USD", transactionId: data.orderID, itemType: "gold" });
                toast.success(`${Number(r.data.amount).toLocaleString()} gold added to your account!`);
                await refreshUser();
              } catch (e) { toast.error(apiErr(e)); }
            }}
            onError={() => toast.error("PayPal error. Please try again.")}
          />
        </PayPalScriptProvider>
      </div>
    </div>
  );
}
